import { useMemo } from 'react';
import { Editor as SlateEditor, Element, Node } from 'slate';
import { TagWithMetadata } from 'entities/tag';
import { i18n } from 'shared/i18n';
import { getBlockTagBindings } from 'slate/extensions/tag/types/block';
import { buildChainContaining } from '../Footer/tagChainUtils';

export interface TaggedChainValue {
  tagId: string;
  value: string;
  list?: { ordered: boolean; items: string[] };
  location: { section: string; page: number };
  unplaced?: boolean;
}

interface ReferenceCell {
  tags?: string[];
  value?: unknown;
}
type ReferenceRow = (ReferenceCell | null | undefined)[];
interface ReferenceEntry {
  superId?: string;
  selectionContent?: ReferenceRow[];
}

export function useTaggedChainValues(
  editor: SlateEditor,
  activeTag: TagWithMetadata | null,
  documentTags: TagWithMetadata[] | undefined,
  references: ReferenceEntry[] | null | undefined
): TaggedChainValue[] {
  return useMemo(() => taggedChainValues(editor, activeTag, documentTags, references), [editor, activeTag, documentTags, references]);
}

function taggedChainValues(
  editor: SlateEditor,
  activeTag: TagWithMetadata | null,
  documentTags: TagWithMetadata[] | undefined,
  references: ReferenceEntry[] | null | undefined
): TaggedChainValue[] {
  if (!activeTag) return [];

  const chain = buildChainContaining(documentTags ?? [], activeTag.id);
  if (chain.length === 0) return [];

  const stripExt = (s: string) => s.replace(/\.[^./\\]+$/, '');

  const findInlineText = (tagId: string): string | null => {
    try {
      const entry = Array.from(
        SlateEditor.nodes(editor, {
          at: [],
          match: (n) => Element.isElement(n) && n.type === 'tag' && n.tagId === tagId,
        })
      )[0];
      if (!entry) return null;
      return Node.string(entry[0]);
    } catch {
      return null;
    }
  };

  const findElementNode = (uuid: string | null | undefined): Node | null => {
    if (!uuid) return null;
    try {
      const entry = Array.from(
        SlateEditor.nodes(editor, {
          at: [],
          match: (n) => Element.isElement(n) && n.uuid === uuid,
        })
      )[0];
      return entry ? entry[0] : null;
    } catch {
      return null;
    }
  };

  const findTableCellValue = (tag: TagWithMetadata): string | null => {
    const ref = (references ?? []).find((r) => r.superId === tag.onId);
    if (!ref?.selectionContent) return null;
    for (const row of ref.selectionContent) {
      for (const cell of row ?? []) {
        if (Array.isArray(cell?.tags) && cell.tags.includes(tag.id)) {
          return String(cell.value ?? '');
        }
      }
    }
    return null;
  };

  const values: TaggedChainValue[] = [];
  for (const tag of chain) {
    const carrier = tag.metadata?.blockCarrier;
    let value = '';
    let list: { ordered: boolean; items: string[] } | undefined;
    let unplaced = false;
    if (tag.id !== activeTag.id && tag.metadata?.unplaced) {
      unplaced = true;
    } else if (carrier?.kind === 'image') {
      value = `${i18n('tag:content.chain.image')}: ${stripExt(carrier.displayName)}`;
    } else if (carrier?.kind === 'reference-table') {
      value = `${i18n('tag:content.chain.table')}: ${carrier.displayName}`;
    } else {
      const inline = findInlineText(tag.id);
      const cellValue = inline == null ? findTableCellValue(tag) : null;
      if (inline != null) {
        value = inline;
      } else if (cellValue) {
        value = cellValue;
      } else {
        const found = findElementNode(tag.onId);
        const node = found && Element.isElement(found) && getBlockTagBindings(found).some((b) => b.tagId === tag.id) ? found : null;
        if (node && (node.type === 'bulleted-list' || node.type === 'numbered-list')) {
          const items = node.children
            .filter((c) => Element.isElement(c) && c.type === 'list-item')
            .map((li) => Node.string(li).trim())
            .filter((t) => t.length > 0);
          list = { ordered: node.type === 'numbered-list', items };
          value = items.join(' ');
        } else if (node) {
          value = Node.string(node).trim();
        } else if (tag.id !== activeTag.id && !tag.hidden && tag.metadata && tag.metadata.textSnippet === undefined) {
          unplaced = true;
        } else {
          value = tag.metadata?.textSnippet ?? (tag.hidden ? tag.value : null) ?? '';
        }
      }
    }
    values.push({
      tagId: tag.id,
      value,
      list,
      ...(unplaced && { unplaced }),
      location: {
        section: tag.metadata?.sectionName || '',
        page: tag.metadata?.pageNumber || 0,
      },
    });
  }
  return values;
}
